import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES = {
    info: { className: 'bg-info-subtle text-info-strong', Icon: Info },
    success: { className: 'bg-success-subtle text-success-strong', Icon: CircleCheck },
    warning: { className: 'bg-warning-subtle text-warning-strong', Icon: TriangleAlert },
    danger: { className: 'bg-destructive-subtle text-destructive-strong', Icon: CircleAlert },
} as const;

/**
 * A notice on the tint of its functional colour, with an icon naming the tone
 * so it does not rely on colour alone (docs/10 §4). Never orange: that is the
 * primary action's. Errors interrupt; everything else is announced politely.
 *
 * It is the one look a notice has in the panels, and it carries no placement —
 * no margin, width or position — so a toast, when the panels gain one, shows
 * this same notice rather than a look of its own.
 */
export function Alert({ tone = 'info', className, children }: { tone?: AlertTone; className?: string; children: ReactNode }) {
    const { className: toneClassName, Icon } = TONES[tone];
    return (
        <div
            data-slot="alert"
            role={tone === 'danger' ? 'alert' : 'status'}
            className={cn('flex items-start gap-3 rounded-card px-4 py-3 text-13', toneClassName, className)}
        >
            <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
}
